import { useState, type CSSProperties } from "react";
import { useI18n } from "./i18n";
import type { GameFacade, HudSnapshot } from "./GameFacade";
import type { TowerConfig, EnemyConfig } from "@bastion/data";

interface SkillPanelProps {
  facade: GameFacade;
  snapshot: HudSnapshot;
  towerConfigs: Record<string, TowerConfig>;
  enemyConfigs: Record<string, EnemyConfig>;
}

/**
 * 右侧技能面板：显示所有可用技能及其图标、名称、费用、冷却状态和使用按钮。
 * 当选中塔或敌人时，在其下方显示完整属性。
 */
export function SkillPanel({ facade, snapshot, towerConfigs, enemyConfigs }: SkillPanelProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const skills = snapshot.skills;
  const { t, tEntity } = useI18n();

  const selected = selectedId ? (skills.find((s) => s.id === selectedId) ?? null) : null;

  // 检查游戏中是否选中有敌人
  const selectedEnemyType = snapshot.selectedEnemyType;
  const selectedEnemyConfig = selectedEnemyType ? enemyConfigs[selectedEnemyType] : null;

  // 检查游戏中是否选中了塔
  const selectedTowerType = snapshot.selectedTowerType;
  const selectedTowerConfig = selectedTowerType ? towerConfigs[selectedTowerType] : null;
  const selectedTowerStats = snapshot.selectedTowerStats;

  function formatHp(value: number): string {
    return Number.isFinite(value) ? value.toFixed(1) : "0.0";
  }

  // 技能可用条件：就绪且金币足够
  function canUseSkill(skill: ReturnType<typeof skills.find>) {
    return skill?.ready && snapshot.gold >= skill.cost;
  }

  // 点击技能按钮：可释放则直接通过渲染器进入瞄准模式并触发，否则只切换选中查看详情
  function handleSkillClick(skillId: string) {
    const skill = skills.find((s) => s.id === skillId);
    const alreadySelected = selectedId === skillId;
    if (alreadySelected) {
      setSelectedId(null);
      return;
    }
    if (!skill || !canUseSkill(skill)) {
      setSelectedId(skillId);
      return;
    }
    // 有半径的技能走渲染器瞄准模式；无半径的直接释放
    if (skill.radius !== null && skill.radius !== undefined) {
      facade.startSkillAim(skillId);
    } else {
      facade.useSkill(skillId);
    }
  }

  // 如果选中了敌人，获取当前敌人的生命值
  const enemyHp = snapshot.selectedEnemyHp;

  return (
    <aside className="skill-panel" data-testid="skill-panel">
      <section className="panel-section">
        <h3 className="panel-title">{t("skill_panel_title")}</h3>
        {skills.map((skill) => {
          const icon =
            skill.id === "area_damage"
              ? "☄️"
              : skill.id === "slow_all"
                ? "❄️"
                : skill.id === "heal_lives"
                  ? "💚"
                  : "🔧";
          const isSelected = selectedId === skill.id;
          const usable = canUseSkill(skill);
          const cooldownProgress =
            skill.cooldown > 0 ? Math.min(1, skill.cooldownRemaining / skill.cooldown) : 0;
          return (
            <div key={skill.id}>
              <button
                className={`skill-btn ${!skill.ready ? "cooldown" : ""} ${isSelected ? "active" : ""} ${!usable && skill.ready && !isSelected ? "disabled" : ""}`}
                data-testid={`skill-${skill.id}`}
                onClick={() => handleSkillClick(skill.id)}
                disabled={!skill.ready && !isSelected}
              >
                <span className="skill-icon-wrap">
                  <span className="skill-icon">{icon}</span>
                </span>
                <span className="skill-name">{tEntity("skill", skill.id)}</span>
                <span className="skill-cost">🪙 {skill.cost}</span>
                {!skill.ready && (
                  <>
                    <span className="skill-cooldown" data-testid={`skill-cooldown-${skill.id}`}>
                      {t("skill_cd")}
                    </span>
                    <span
                      className="skill-cooldown-mask"
                      data-testid={`skill-cooldown-mask-${skill.id}`}
                      style={{ "--cooldown-progress": cooldownProgress } as CSSProperties}
                      aria-hidden="true"
                    />
                  </>
                )}
              </button>
              {isSelected && selected && (
                <div className="skill-detail" data-testid={`skill-detail-${skill.id}`}>
                  {!usable ? (
                    <span className="skill-desc">
                      {!skill.ready ? t("skill_on_cd") : t("skill_insufficient_gold")}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Enemy Stats Display */}
      {selectedEnemyConfig && enemyHp != null && (
        <section className="panel-section enemy-stats-section" data-testid="enemy-stats">
          <h3 className="panel-title">
            {selectedEnemyConfig.isBoss ? `${t("enemy_boss")} ` : ""}
            {tEntity("enemy", selectedEnemyType!)}
          </h3>
          <div className="stats-grid">
            <div className="stat-row">
              <span className="stat-label">{t("stat_hp")}:</span>
              <span className="stat-value">
                {formatHp(enemyHp.current)}/{formatHp(enemyHp.max)}
              </span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_speed")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.speed ?? "—"}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_attack")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.attack ?? "—"}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_magic")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.magic ?? 0}</span>
            </div>
            {selectedEnemyConfig.stats?.defense !== undefined && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_defense")}:</span>
                <span className="stat-value">{selectedEnemyConfig.stats.defense}</span>
              </div>
            )}
            {selectedEnemyConfig.stats?.magicResistance !== undefined && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_magic_resistance")}:</span>
                <span className="stat-value">{selectedEnemyConfig.stats.magicResistance}</span>
              </div>
            )}
            <div className="stat-row">
              <span className="stat-label">{t("stat_accuracy")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.accuracy ?? 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_resistance")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.resistance ?? 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_crit_rate")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.critRate ?? 0}%</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_crit_damage")}:</span>
              <span className="stat-value">{selectedEnemyConfig.stats?.critDamage ?? "—"}x</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("enemy_gold")}:</span>
              <span className="stat-value">🪙 {selectedEnemyConfig.goldReward}</span>
            </div>
          </div>
        </section>
      )}

      {/* Tower Stats Display */}
      {selectedTowerConfig && selectedTowerStats && (
        <section className="panel-section tower-stats-section" data-testid="tower-stats">
          <h3 className="panel-title">{tEntity("tower", selectedTowerType!)}</h3>
          <div className="stats-grid">
            {/* UnitStats - Core Combat Attributes */}
            <div className="stat-row">
              <span className="stat-label">{t("stat_attack")}:</span>
              <span className="stat-value">{selectedTowerStats.attack}</span>
            </div>
            {selectedTowerConfig.stats.magic > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_magic")}:</span>
                <span className="stat-value">{selectedTowerStats.magic}</span>
              </div>
            )}
            {selectedTowerConfig.stats.defense > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_defense")}:</span>
                <span className="stat-value">{selectedTowerStats.defense}</span>
              </div>
            )}
            {selectedTowerConfig.stats.magicResistance > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_magic_resistance")}:</span>
                <span className="stat-value">{selectedTowerStats.magicResistance}</span>
              </div>
            )}
            {selectedTowerConfig.stats.accuracy > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_accuracy")}:</span>
                <span className="stat-value">{selectedTowerStats.accuracy}</span>
              </div>
            )}
            {selectedTowerConfig.stats.resistance > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_resistance")}:</span>
                <span className="stat-value">{selectedTowerStats.resistance}</span>
              </div>
            )}
            <div className="stat-row">
              <span className="stat-label">{t("stat_attack_speed")}:</span>
              <span className="stat-value">{selectedTowerStats.attackSpeed}/s</span>
            </div>
            {selectedTowerStats.attackSpeedMultiplier > 1 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_attack_speed_boost")}:</span>
                <span className="stat-value">
                  ×{selectedTowerStats.attackSpeedMultiplier.toFixed(2)} (
                  {selectedTowerStats.activeAttackSpeedBoosts
                    .map((boost) => `${boost.remaining.toFixed(1)}s`)
                    .join(", ")}
                  )
                </span>
              </div>
            )}
            {selectedTowerStats.critRate > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_crit_rate")}:</span>
                <span className="stat-value">{selectedTowerStats.critRate}%</span>
              </div>
            )}
            {selectedTowerStats.critDamage > 1 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_crit_damage")}:</span>
                <span className="stat-value">{selectedTowerStats.critDamage}x</span>
              </div>
            )}
            {/* Tower-specific attributes */}
            <div className="stat-row">
              <span className="stat-label">{t("stat_range")}:</span>
              <span className="stat-value">{selectedTowerStats.range}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_projectile_speed")}:</span>
              <span className="stat-value">{selectedTowerConfig.projectileSpeed}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_max_level")}:</span>
              <span className="stat-value">{selectedTowerConfig.maxLevel}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_cost")}:</span>
              <span className="stat-value">🪙 {selectedTowerConfig.cost}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t("stat_sell_refund")}:</span>
              <span className="stat-value">
                {Math.round(selectedTowerConfig.sellRefundRate * 100)}%
              </span>
            </div>
            {selectedTowerConfig.slowOnHit !== undefined && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_slow")}:</span>
                <span className="stat-value">
                  {Math.round((1 - selectedTowerConfig.slowOnHit) * 100)}% for{" "}
                  {selectedTowerConfig.slowDuration}s
                </span>
              </div>
            )}
            {selectedTowerStats.chainCount > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_chain")}:</span>
                <span className="stat-value">×{selectedTowerStats.chainCount}</span>
              </div>
            )}
            {selectedTowerStats.splashRadius > 0 && (
              <div className="stat-row">
                <span className="stat-label">{t("stat_splash")}:</span>
                <span className="stat-value">{selectedTowerStats.splashRadius}u</span>
              </div>
            )}
          </div>
          {/* 升级按钮 */}
          {snapshot.selectedTowerUpgradeCost !== null &&
            snapshot.selectedTowerUpgradeCost !== undefined && (
              <button
                className="panel-btn"
                data-testid="upgrade-selected"
                onClick={() => {
                  if (snapshot.selectedTowerId) facade.upgradeTower(snapshot.selectedTowerId);
                }}
              >
                {t("build_upgrade", { cost: String(snapshot.selectedTowerUpgradeCost) })}
              </button>
            )}
          {/* 定位描述 */}
          {selectedTowerConfig.position && (
            <p className="panel-hint" data-testid="tower-position">
              {t("stat_position")}：{tEntity("tower_position", selectedTowerConfig.id)}
            </p>
          )}
          {snapshot.selectedTowerSkills.length > 0 && (
            <div className="panel-hint tower-skills" data-testid="tower-skills">
              {snapshot.selectedTowerSkills.map((skill) => (
                <p className="tower-skill" data-testid={`tower-skill-${skill.id}`} key={skill.id}>
                  {skill.icon ? `${skill.icon} ` : ""}
                  {skill.description
                    ? tEntity("tower_skill", skill.id)
                    : tEntity("skill", skill.id)}
                  {skill.threshold > 0 ? ` · ${skill.progress}/${skill.threshold}` : ""}
                  {skill.ready ? ` · ${t("skill_ready")}` : ""}
                </p>
              ))}
            </div>
          )}
        </section>
      )}
    </aside>
  );
}
